import { memo, useCallback, useContext, useEffect, useRef, useState, type CSSProperties } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDocThumb } from '../hooks/useDocThumb';
import { useSiteShot } from '../hooks/useSiteShot';
import { assetContent, contentProps, track } from '../lib/analytics';
import { universeApi } from '../lib/api';
import { fallbackGradient, initials, typeLabel } from '../lib/format';
import { ListAnalyticsContext, listProps, observeImpression } from '../lib/impressions';
import { observeReveal } from '../lib/motion';
import { strings } from '../lib/strings';
import type { UniverseAsset } from '../lib/types';
import { normalizeUrl } from '../lib/web';
import { HoverPreview } from './HoverPreview';
import { TypeGlyph, TypeTile } from './TypeGlyph';

const HOVER_DELAY_MS = 450;
/** Once a preview has been seen, neighbours open almost instantly (macOS tooltip warmth). */
const WARM_DELAY_MS = 90;
const WARM_WINDOW_MS = 700;
/** Grace period for the pointer to cross the gap between disc and card. */
const CLOSE_GRACE_MS = 240;
const EXIT_MS = 170;

let lastClosedAt = 0;
let closeActive: (() => void) | null = null;

/** Cover Studio focal point and zoom; `scale` composes with the image's own transforms. */
function focusStyle(focus: UniverseAsset['thumbnail_focus']): CSSProperties | undefined {
  if (!focus) return undefined;
  const origin = `${focus.x * 100}% ${focus.y * 100}%`;
  return { objectPosition: origin, transformOrigin: origin, scale: focus.zoom > 1 ? String(focus.zoom) : undefined };
}

function canHover(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

function UniverseDiscBase({ asset, index }: { asset: UniverseAsset; index: number }) {
  const location = useLocation();
  const list = useContext(ListAnalyticsContext);
  const linkRef = useRef<HTMLAnchorElement>(null);
  const openTimer = useRef<number | null>(null);
  const closeTimer = useRef<number | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const [closing, setClosing] = useState(false);

  const itemRef = useRef<HTMLLIElement>(null);
  const [nearView, setNearView] = useState(false);
  const artwork = asset.thumbnail_url ?? asset.cover_url;
  const siteUrl = asset.type === 'link' && !artwork ? normalizeUrl(asset.external_url) : null;
  // Audio files arrive as `document` without a preview; their real type is only known from the detail.
  const typeUnknown = asset.type === 'document' && !asset.preview_url;
  const wantsThumb = !artwork && asset.type !== 'link' && asset.type !== 'video' && asset.type !== 'audio';
  const [type, setType] = useState(asset.type);
  const [sheetLoaded, setSheetLoaded] = useState(false);
  const [sheetWide, setSheetWide] = useState(false);

  useEffect(() => {
    if (!typeUnknown || !nearView) return;
    let alive = true;
    universeApi
      .getAsset(asset.id)
      .then((d) => alive && setType(d.type))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [asset.id, nearView, typeUnknown]);

  useEffect(() => {
    const el = itemRef.current;
    if (!el || !(siteUrl || typeUnknown || wantsThumb)) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setNearView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '300px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [siteUrl, typeUnknown, wantsThumb]);

  const playable = type === 'video' || type === 'audio';
  const shot = useSiteShot(siteUrl, nearView);
  const thumb = useDocThumb(asset.id, wantsThumb && nearView);
  const image = artwork ?? shot.src ?? (thumb?.fill === 'cover' ? thumb.src : null);
  const showImage = Boolean(image) && !failed;
  const sheet = !image && thumb?.fill === 'sheet' ? thumb.src : null;

  const cancelOpen = () => {
    if (openTimer.current !== null) window.clearTimeout(openTimer.current);
    openTimer.current = null;
  };
  const cancelClose = () => {
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };

  const hideNow = useCallback(() => {
    cancelOpen();
    cancelClose();
    setAnchor(null);
    setClosing(false);
    if (closeActive === hideNow) closeActive = null;
  }, []);

  const close = useCallback(() => {
    cancelOpen();
    cancelClose();
    lastClosedAt = Date.now();
    if (closeActive === hideNow) closeActive = null;
    setClosing(true);
    closeTimer.current = window.setTimeout(hideNow, EXIT_MS);
  }, [hideNow]);

  const show = useCallback(() => {
    const el = linkRef.current;
    if (!el) return;
    cancelClose();
    if (closeActive && closeActive !== hideNow) closeActive();
    closeActive = hideNow;
    setClosing(false);
    setAnchor(el.getBoundingClientRect());
    universeApi.prefetchAsset(asset.id);
    track('preview_opened', { ...contentProps(assetContent(asset)), ...listProps(list, index) });
  }, [asset, hideNow, list, index]);

  const keepOpen = useCallback(() => {
    cancelClose();
    setClosing(false);
  }, []);

  const scheduleClose = useCallback(() => {
    cancelOpen();
    cancelClose();
    closeTimer.current = window.setTimeout(close, CLOSE_GRACE_MS);
  }, [close]);

  const scheduleOpen = () => {
    if (!canHover()) return;
    if (anchor && !closing) {
      keepOpen();
      return;
    }
    cancelOpen();
    const warm = Date.now() - lastClosedAt < WARM_WINDOW_MS;
    openTimer.current = window.setTimeout(show, warm ? WARM_DELAY_MS : HOVER_DELAY_MS);
  };

  useEffect(() => {
    if (!anchor) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('scroll', hideNow, { passive: true });
    window.addEventListener('blur', hideNow);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', hideNow);
      window.removeEventListener('blur', hideNow);
      window.removeEventListener('keydown', onKey);
    };
  }, [anchor, close, hideNow]);

  useEffect(() => hideNow, [hideNow]);

  useEffect(() => (itemRef.current ? observeReveal(itemRef.current) : undefined), []);

  const sphereRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const el = sphereRef.current;
    if (!el || !list) return;
    return observeImpression(el, assetContent(asset), index, list);
  }, [asset, index, list]);

  const previewing = Boolean(anchor) && !closing;

  return (
    <li ref={itemRef} className="reveal list-none">
      <Link
        ref={linkRef}
        to={`/view/${asset.id}`}
        state={{ background: location }}
        data-previewing={previewing}
        className="disc-focus group flex flex-col items-center rounded-md text-center"
        onMouseEnter={scheduleOpen}
        onMouseLeave={scheduleClose}
        onFocus={(e) => {
          if (e.currentTarget.matches(':focus-visible')) show();
        }}
        onBlur={close}
        onClick={(e) => {
          hideNow();
          track('content_selected', {
            ...contentProps(assetContent(asset)),
            ...listProps(list, index),
            input: e.detail === 0 ? 'keyboard' : 'pointer',
          });
        }}
      >
        <span
          ref={sphereRef}
          className="sphere disc-ring relative block rounded-full shadow-disc"
          style={{ aspectRatio: '1 / 1' }}
          data-cursor-text={playable ? strings.cursorPlay : strings.cursorView}
          data-cursor-icon={playable ? 'play' : 'view'}
          data-cursor-accent={`var(--type-${type}-deep, var(--color-ink))`}
        >
          <span className="disc-clip absolute inset-0 overflow-hidden rounded-full bg-surface">
            <span className="disc-parallax absolute inset-0">
              <span
                className="absolute inset-0 flex items-center justify-center font-semibold tracking-tight text-white/85"
                style={{
                  background: fallbackGradient(asset.title),
                  fontSize: 'calc(var(--text-xl) * var(--sphere-scale))',
                }}
              >
                {!showImage && !sheetLoaded ? initials(asset.title) : null}
              </span>
              {sheet ? (
                <img
                  src={sheet}
                  alt=""
                  decoding="async"
                  onLoad={(e) => {
                    setSheetWide(e.currentTarget.naturalWidth > e.currentTarget.naturalHeight);
                    setSheetLoaded(true);
                  }}
                  data-loaded={sheetLoaded}
                  data-shape={sheetWide ? 'wide' : 'page'}
                  className="disc-sheet"
                />
              ) : null}
              {showImage && image ? (
                <img
                  src={image}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  onLoad={() => setLoaded(true)}
                  onError={() => setFailed(true)}
                  data-loaded={loaded}
                  className={`disc-image absolute inset-0 h-full w-full object-cover ${artwork ? '' : 'object-top'}`}
                  style={focusStyle(artwork ? asset.thumbnail_focus : null)}
                />
              ) : null}
            </span>
            {playable && (
              <span aria-hidden="true" className="disc-play absolute inset-0 flex items-center justify-center">
                <span
                  className="glass-chip flex items-center justify-center rounded-full text-white"
                  style={{ width: 'calc(48px * var(--sphere-scale))', height: 'calc(48px * var(--sphere-scale))' }}
                >
                  <TypeGlyph
                    type="video"
                    style={{
                      width: 'calc(20px * var(--sphere-scale))',
                      height: 'calc(20px * var(--sphere-scale))',
                      marginLeft: 2,
                    }}
                  />
                </span>
              </span>
            )}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 rounded-full ring-1 ring-inset ring-black/5"
            />
          </span>
          <TypeTile type={type} size="md" className="disc-badge" />
        </span>
        <span
          className={`mt-3 line-clamp-2 w-full px-1 text-sm font-semibold leading-snug tracking-tight ${
            asset.title.trim() ? 'text-ink' : 'italic text-subtle'
          }`}
        >
          {asset.title.trim() || strings.untitled}
        </span>
        <span className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-subtle">
          <TypeGlyph type={type} className="type-ink h-3 w-3" data-type={type} />
          {typeLabel(type)}
        </span>
      </Link>
      {anchor && (
        <HoverPreview
          asset={asset}
          anchor={anchor}
          image={image}
          sheet={sheet}
          closing={closing}
          onPointerEnter={keepOpen}
          onPointerLeave={scheduleClose}
          onOpen={hideNow}
        />
      )}
    </li>
  );
}

export const UniverseDisc = memo(UniverseDiscBase);

export function SkeletonDisc({ index = 0, entering = false }: { index?: number; entering?: boolean }) {
  const delay = `${(index % 7) * 90}ms`;
  return (
    <li
      className={`flex list-none flex-col items-center ${entering ? 'disc-enter' : ''}`}
      style={entering ? { ['--i' as string]: Math.min(index, 8) } : undefined}
      aria-hidden="true"
    >
      <span
        className="sphere skeleton block rounded-full"
        style={{ aspectRatio: '1 / 1', animationDelay: delay }}
      />
      <span className="skeleton mt-3 block h-3 w-3/4 rounded-full" style={{ animationDelay: delay }} />
      <span className="skeleton mt-2 block h-[10px] w-1/3 rounded-full" style={{ animationDelay: delay }} />
    </li>
  );
}
